import { Injectable } from '@angular/core';
import { REPLACE_UPCOMING_CHANGES, SupabaseService } from "./supabase.service";
import { map, shareReplay, switchMap, take } from "rxjs/operators";
import { BusinessService } from "./business.service";
import { AuthService } from "./auth.service";
import { combineLatest, of } from "rxjs";
import { UsersService } from "./users.service";

@Injectable({
  providedIn: 'root'
})
export class QuickbooksService {

  constructor(
    private supabaseService: SupabaseService,
    private businessService: BusinessService,
    private authService: AuthService,
    private usersService: UsersService,
  ) { }

  async testQBApi() {
    const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;
    const user = (await this.authService.user$.pipe(take(1)).toPromise())!;

    return await this.supabaseService.edgeFunc('qb-test-api-call', {
      schema: business.businessId,
      userUid: user.uid,
      appDomain: `${window.location.protocol}//${window.location.host}`
    });
  }

  async getAuthUrl() {
    const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;
    const user = (await this.authService.user$.pipe(take(1)).toPromise())!;

    return await this.supabaseService.edgeFunc('qb-auth', {
      schema: business.businessId,
      userUid: user.uid,
      appDomain: `${window.location.protocol}//${window.location.host}`
    });
  }

  async disconnect() {
    const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;
    const user = (await this.authService.user$.pipe(take(1)).toPromise())!;

    return await this.supabaseService.edgeFunc('qb-disconnect', {
      schema: business.businessId,
      userUid: user.uid
    });
  }

  connections$ = combineLatest([
    this.businessService.selectedBusiness$,
    this.usersService.currentUser$
  ]).pipe(
    switchMap(([businessProfile, user]) => {
      if (!businessProfile || user.role !== 'owner')
        return of(null);

      return this.supabaseService.rpc<any[]>(
        {
          cName: 'qb_connection',
          fn: 'get_qb_details',
          schema: businessProfile.businessId,
          tables: ['qb_connection']
        },
        _ => null,
        'qb_connection'
      );
    }),
    shareReplay(1)
  );

  currentState$ = this.connections$.pipe(
    map(connections => {
      if (!connections) {
        return {
          permissionDenied: true
        };
      }

      if (!connections || connections.length === 0) {
        return {
          hasAccess: false
        };
      }

      const latest = connections.reduce((latest, current) =>
        new Date(current.connectionCreatedAt) > new Date(latest.connectionCreatedAt) ? current : latest
      );

      return {
        hasAccess: latest.connectionRefreshTokenExpiresAt > new Date(),
        expiryDate: latest.connectionRefreshTokenExpiresAt
      };
    })
  );
}
